import { Button, Form, Space, theme, Typography } from "antd";
import { useEffect, useState } from "react";
import { lockUser, unlockUser } from "../repository/auth.repository";
import InModal from "../controls/InModal";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import DynamicForm from "../controls/DynamicForm";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

export interface LockUserProps {
  readonly open?: boolean;
  readonly initialValues: {
    readonly userUid: string;
    readonly userName: string;
    readonly lockoutEnd?: string;
    readonly lockoutEnabled?: boolean;
  };
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function LockUserFormDialog(props: LockUserProps) {
  const { token } = theme.useToken();
  const user = useSelector(authorizeSelectors.user);
  const [form] = Form.useForm();
  const values = Form.useWatch<{
    readonly lockoutEnd: Dayjs;
  }>([], form);
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState(false);

  // Lifecycle
  useEffect(() => {
    if (!props.initialValues.lockoutEnd) {
      form.setFieldsValue({
        lockoutEnd: null,
      });
    }
  }, [props.initialValues.lockoutEnd]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  // Events

  function onSubmit(): void {
    setLoading(true);
    setError(false);
    if (!props.initialValues.lockoutEnd) {
      const { lockoutEnd } = values;
      lockUser({
        userUid: props.initialValues.userUid,
        ...(lockoutEnd ? { lockoutEnd: lockoutEnd.toISOString() } : {}),
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else {
      unlockUser({
        userUid: props.initialValues.userUid,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  return (
    <InModal
      open={props.open}
      title={props.initialValues.lockoutEnd ? "Unlock User" : "Lock User"}
      actions={
        <Space className="flex flex-row items-center justify-end">
          {user?.userId != props.initialValues.userUid &&
            props.initialValues.lockoutEnabled && (
              <>
                <Button disabled={loading} onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  type={"primary"}
                  danger
                  loading={loading}
                  disabled={!valid && !props.initialValues.lockoutEnd}
                  onClick={onSubmit}
                >
                  {props.initialValues.lockoutEnd ? "Unlock" : "Lock"}
                </Button>
              </>
            )}
        </Space>
      }
      onCancel={onCancel}
      size={"small"}
    >
      <Typography.Paragraph>
        {user?.userId != props.initialValues.userUid &&
          props.initialValues.lockoutEnabled && (
            <Typography.Text>
              Continue to {props.initialValues.lockoutEnd ? "unlock" : "lock"}{" "}
              <Typography.Text className="font-bold">
                {props.initialValues?.userName}
              </Typography.Text>{" "}
              user?
            </Typography.Text>
          )}
        {user?.userId == props.initialValues.userUid &&
          props.initialValues.lockoutEnabled && (
            <Typography.Text>
              You can't {props.initialValues.lockoutEnd ? "unlock" : "lock"}{" "}
              yourself.
            </Typography.Text>
          )}
        {!props.initialValues.lockoutEnabled && (
          <Typography.Text>
            User cannot be{" "}
            {props.initialValues.lockoutEnd ? "unlocked" : "locked"}.
          </Typography.Text>
        )}
      </Typography.Paragraph>
      {user?.userId != props.initialValues.userUid &&
        props.initialValues.lockoutEnabled && (
          <>
            {!props.initialValues.lockoutEnd && (
              <DynamicForm
                className="w-64"
                form={form}
                schema={{
                  groups: [
                    {
                      controls: [
                        {
                          type: "datetimepicker",
                          label: "Lock Until",
                          name: "lockoutEnd",
                          placeholder: "Indefinitely",
                        },
                      ],
                    },
                  ],
                }}
              />
            )}
            <ModalErrorAlert
              message={error ? "Something went wrong, try again" : undefined}
            />
          </>
        )}
    </InModal>
  );
}

export default LockUserFormDialog;
